import { updateApplication } from "ee/actions/applicationActions";
import type { ApplicationPayload } from "ce/constants/ReduxActionConstants";
import { Switch } from "design-system";
import { isEmpty, debounce } from "lodash";
import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAppsmithConfigs } from "ee/configs";
import PropertyHelpLabel from "pages/Editor/PropertyPane/PropertyHelpLabel";
import ColorPickerComponent from "components/propertyControls/ColorPickerComponentV2";
import {
  InputNumber,
  Slider,
  Input,
  Form,
  Flex,
  Switch as AntdSwitch,
} from "antd";
import type { Color } from "antd/es/color-picker";
import { getCurrentApplicationId } from "selectors/editorSelectors";
import { getCurrentApplication } from "ee/selectors/applicationSelectors";

interface WaterMarkProps {
  // application: ApplicationPayload;
}

interface WatermarkConfig {
  show: boolean;
  content: string;
  showTime: boolean;
  color: string | Color;
  fontSize: number;
  gap: [number, number];
}

const defaultConfig: WatermarkConfig = {
  show: false,
  content: "",
  showTime: false,
  color: "rgba(0, 0, 0, 0.08)",
  fontSize: 12,
  gap: [100, 100],
};

const applicationWaterMark: React.FC<WaterMarkProps> = () => {
  const [form] = Form.useForm();
  const configRef = useRef<WatermarkConfig>(defaultConfig);
  const [config, setConfig] = useState<WatermarkConfig>(defaultConfig);
  const [switchControl, SetSwitchControl] = useState<boolean>(false);
  const dispatch = useDispatch();
  // const currentUser: User | undefined = useSelector(getCurrentUser);
  // const socialLoginList = useSelector(getThirdPartyAuths);

  const [autoFocus, setAutoFocus] = useState(false);
  const currentApplicationId: string = useSelector(getCurrentApplicationId);
  const application: ApplicationPayload = useSelector(
    getCurrentApplication,
  ) as ApplicationPayload;

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // const selectedTheme = useSelector(getSelectedAppTheme);
  const [selectedColor, setSelectedColor] = useState<string>("");
  const [isFullColorPicker, setFullColorPicker] = useState(false);

  const onChangeSwitch = (val: boolean) => {
    SetSwitchControl(val);
    if (val) {
      dispatch(
        updateApplication(currentApplicationId, {
          waterMarkConfig: { ...configRef.current, show: val },
        }),
      );
    } else {
      dispatch(
        updateApplication(currentApplicationId, {
          waterMarkConfig: {},
        }),
      );
    }
  };

  useEffect(() => {
    if (!isEmpty(application.waterMarkConfig)) {
      const newConfig: WatermarkConfig = {
        ...defaultConfig,
        ...application.waterMarkConfig,
      };
      SetSwitchControl(true);
      setConfig(newConfig);
      setSelectedColor(newConfig.color as string);
      form.setFieldsValue(newConfig);
    }
  }, [application.waterMarkConfig, form]);

  const changeAndUpdateApplication = useRef(
    debounce((values: Partial<WatermarkConfig>) => {
      const merged = { ...configRef.current, ...values };
      setConfig(merged);
      configRef.current = merged;
      dispatch(
        updateApplication(currentApplicationId, {
          waterMarkConfig: merged,
        }),
      );
    }, 500),
  ).current;

  return (
    <>
      <div className="pb-4">
        <div className="flex justify-between items-center">
          <Switch
            data-testid={"forking-enabled-toggle"}
            isSelected={switchControl}
            onChange={onChangeSwitch}
          >
            <PropertyHelpLabel
              label={"显示水印"}
              lineHeight="1.17"
              maxWidth="270px"
              tooltip={"开启水印并完成配置"}
            />
          </Switch>
        </div>
        {switchControl && (
          <>
            <Form
              style={{
                flexShrink: 0,
                marginTop: 16,
              }}
              form={form}
              layout="horizontal"
              initialValues={config}
              onValuesChange={(_, values) => {
                if (values.color) {
                  setSelectedColor(values.color);
                }
                changeAndUpdateApplication({
                  ...values,
                  show: switchControl,
                });
              }}
            >
              <Form.Item name="content" label="水印文案">
                <Input placeholder="请输入水印文案" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="showTime" label="显示时间">
                <AntdSwitch />
              </Form.Item>
              <Form.Item name="color" label="水印颜色">
                <ColorPickerComponent
                  autoFocus={autoFocus}
                  changeColor={(color: string) => {
                    changeAndUpdateApplication({
                      color,
                      show: switchControl,
                    });
                  }}
                  color={selectedColor}
                  isFullColorPicker={isFullColorPicker}
                  isOpen={autoFocus}
                  key={selectedColor}
                  onPopupClosed={() => setAutoFocus(false)}
                  portalContainer={
                    document.getElementById("app-settings-portal") || undefined
                  }
                  setFullColorPicker={setFullColorPicker}
                />
              </Form.Item>
              <Form.Item name="fontSize" label="水印大小">
                <Slider step={1} min={1} max={100} />
              </Form.Item>
              <Form.Item name="Gap" label="水印间隔">
                <Flex gap="small">
                  <Form.Item name={["gap", 0]}>
                    <InputNumber placeholder="gapX" style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item name={["gap", 1]}>
                    <InputNumber placeholder="gapY" style={{ width: "100%" }} />
                  </Form.Item>
                </Flex>
              </Form.Item>
            </Form>
          </>
        )}
      </div>
    </>
  );
};

export default applicationWaterMark;
